// @flow

import * as React from 'react';
import R from 'ramda';

import PermissionsTable from './PermissionsTable';
import { AccessLevelTooltip } from './AccessLevelTooltip';

import Input from '../core/Input';
import Checkbox from '../core/Checkbox';
import Dropdown from '../core/Dropdown';
import {
  selectableRoleOptions,
  SELECTABLE_AGENCY_ROLE_OPTIONS,
  READ_PERMISSION,
  WRITE_PERMISSION,
  SENSITIVE_DATA,
  ROLE_IDS
} from '../../support/accessControlConstants';
import type {
  UiRoleId,
  Permissions
} from '../../../main/types/accessControlTypes';

type Props = {
  addingAgencyUser?: boolean,
  addingAgencyRelationship?: boolean,
  displayTitle?: boolean,
  firstName?: string,
  lastName?: ?string,
  namesDisabled?: boolean,
  onDataChange: (key: string, value: any) => void,
  onRoleChange: (uiRoleId: UiRoleId) => void,
  permissions: Permissions,
  uiRoleId: UiRoleId,
  defaultCustom?: boolean
};

type State = {
  editableTable: boolean
};

class EditAccessRoleUI extends React.PureComponent<Props, State> {
  state = {
    editableTable: this.props.uiRoleId === ROLE_IDS.CUSTOM
  };

  componentWillReceiveProps = (newProps: Props) => {
    this.setState({ editableTable: newProps.uiRoleId === ROLE_IDS.CUSTOM });
  };

  _onRoleChange = (option: { value: UiRoleId }) => {
    const uiRoleId = option && option.value;
    this.props.onRoleChange(uiRoleId);

    // The table should only be editable when 'Custom' is selected
    // (unless the user presses the 'Edit' button while on another role)
    this.setState({ editableTable: uiRoleId === ROLE_IDS.CUSTOM });
  };

  _onEditButtonClick = () => {
    this.setState({ editableTable: true });
  };

  /**
   * Update permissions
   * permission can be 0 (no boxes checked), 1 ('View' checked only),
   * or 2 (both 'View' and 'Edit' checked).
   * If resource is SENSITIVE_DATA, then permission can either be 0 or 2.
   * 2 and 1 for sensitive data will have same effect
   * @param  {string} resourceName
   * @param  {string} boxType
   */
  _onCheckboxClick = (
    resourceName: string,
    boxType: 'read' | 'write'
  ) => () => {
    const current = this.props.permissions[resourceName];
    let newPermission = 0;

    if (boxType === 'read' && !current) {
      newPermission = READ_PERMISSION;
      if (resourceName === SENSITIVE_DATA) {
        newPermission = WRITE_PERMISSION;
      }
    }
    if (boxType === 'write') {
      // 'Edit' and 'View' should both be checked anytime
      // the user clicks an empty 'Edit' box
      if (!current || current === READ_PERMISSION) {
        newPermission = WRITE_PERMISSION;
      }
      // unchecking 'Edit' should still keep 'View' checked.
      if (current === WRITE_PERMISSION) {
        newPermission = READ_PERMISSION;
      }
    }
    const permissions = { ...this.props.permissions };
    permissions[resourceName] = newPermission;
    // anytime we change a checkbox the dropdown should show 'Custom'
    this.props.onDataChange('permissions', permissions);
    this.props.onDataChange('uiRoleId', 5);
  };

  _getOptionWithDescription = (option: {
    label: string,
    description: string
  }) => (
    <div className="h_m-5 access-dropdown__option-container">
      <div className="access-dropdown__option-label">{option.label}</div>
      <div className="access-dropdown__option-description">
        {option.description}
      </div>
    </div>
  );

  render() {
    const props = this.props;
    const roleOptions = props.addingAgencyUser
      ? SELECTABLE_AGENCY_ROLE_OPTIONS
      : selectableRoleOptions(props.addingAgencyRelationship);
    return (
      <div>
        {props.firstName !== undefined ? (
          <div className="h_d-f h_jc-sb">
            <div className="access-control__input h_w-50 h_pend-10">
              <Input
                value={props.firstName}
                onChange={e => props.onDataChange('firstName', e.value)}
                disabled={props.namesDisabled}
                label="First Name"
                testid="access-control__firstName"
              />
            </div>

            <div className="access-control__input h_w-50">
              <Input
                value={props.lastName}
                onChange={e => props.onDataChange('lastName', e.value)}
                disabled={props.namesDisabled}
                label="Last Name"
                testid="access-control__lastName"
              />
            </div>
          </div>
        ) : null}
        <div className="h_d-f h_jc-sb h_ai-c">
          <Dropdown
            label="Access Level"
            tooltip={<AccessLevelTooltip />}
            value={props.uiRoleId}
            options={roleOptions}
            onChange={this._onRoleChange}
            outerClassName="access-dropdown__select-container"
            className="access-dropdown__select-component"
            optionRenderer={this._getOptionWithDescription}
            clearable={false}
            i13nLabel="Team Role Options"
            testid="team-role-options-dropdown"
          />
          <div className="export__container">
            <Checkbox
              checked={
                R.path(['permissions', 'export'], props) === READ_PERMISSION
              }
              disabled={!this.state.editableTable}
              onChange={this._onCheckboxClick('export', 'read')}
              id="export"
              className="export__checkbox"
              label="Export"
              tooltip="Check to allow user to export sensitive data from pages they can view."
            />
          </div>
        </div>
        {!props.addingAgencyUser ? (
          <PermissionsTable
            permissions={props.permissions}
            disabled={!this.state.editableTable}
            displayTitle={props.displayTitle}
            onEditClick={this._onEditButtonClick}
            onCheckboxClick={this._onCheckboxClick}
          />
        ) : null}
      </div>
    );
  }
}

export default EditAccessRoleUI;
